import React from 'react';
import { Component } from '@/types';
import { AdvancedTable } from '@/components/tables';
import { useComponentDataResolver } from '@/services/component-data/useComponentDataResolver';
import type { ComponentDataResolverResult } from '@/services/component-data/componentDataResolver';

export interface SharedAdvancedTableComponentProps {
  component: Component;
  isViewerMode?: boolean;
  inheritedDataResolver?: ComponentDataResolverResult;
}

interface SharedAdvancedTableResolvedProps {
  component: Component;
  dataResolver: ComponentDataResolverResult;
}

const SharedAdvancedTableResolved: React.FC<SharedAdvancedTableResolvedProps> = ({
  component,
  dataResolver,
}) => {
  const { properties = {} } = component;

  // Loading state
  if (dataResolver.loading && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="advanced-table-loading"
        className="w-full h-full flex items-center justify-center gap-2 text-muted-foreground text-sm"
      >
        <svg
          className="animate-spin"
          style={{ width: 20, height: 20 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading table data...
      </div>
    );
  }

  // Error state
  if (dataResolver.error && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="advanced-table-error"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span className="text-sm text-destructive">{dataResolver.error.message}</span>
        <button
          onClick={() => dataResolver.refetch()}
          style={{
            fontSize: 12,
            color: '#3b82f6',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Build a component object with resolved data for AdvancedTable
  const tableComponent: Component = {
    ...component,
    properties: {
      ...properties,
      data: dataResolver.rows,
    },
  };

  return <AdvancedTable component={tableComponent} />;
};

const SharedAdvancedTableStandalone: React.FC<
  Pick<SharedAdvancedTableComponentProps, 'component'>
> = ({ component }) => {
  const dataResolver = useComponentDataResolver(component);
  return (
    <SharedAdvancedTableResolved
      component={component}
      dataResolver={dataResolver}
    />
  );
};

export const SharedAdvancedTableComponent: React.FC<SharedAdvancedTableComponentProps> = ({
  component,
  inheritedDataResolver,
}) => {
  if (inheritedDataResolver !== undefined) {
    return (
      <SharedAdvancedTableResolved
        component={component}
        dataResolver={inheritedDataResolver}
      />
    );
  }

  return <SharedAdvancedTableStandalone component={component} />;
};

SharedAdvancedTableComponent.displayName = 'SharedAdvancedTableComponent';
